// CyclesTab — Phase 5 Task 12 port of docs/mockups/leaderboard-detail.html
// states starting at L802 (default), L1059 (closed cycle), L1282 (archived),
// L1427 (empty), L1531 (Delete modal), L1730 (Adjust modal).
//
// Layout: 320px cycle list on the left + cycle panel on the right with TopTable
// and per-row score moderation (Delete / Adjust modals). The Reason textarea
// is intentionally NOT sent to the backend — the Go-truth `deleteScore` takes
// no body and `adjustScore` accepts only `{ score: number }`. A `reason` column
// on the audit row is planned as a Phase 5.3d backend extension. See the
// helper text below each Reason textarea.

import { useEffect, useMemo, useState } from "react";
import { Menu } from "../../../components/Menu/Menu";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3, Trash2 } from "lucide-react";

import { Alert } from "../../../components/Alert/Alert";
import { Modal } from "../../../components/Modal/Modal";
import { Button } from "../../../components/Button/Button";
import { Skeleton } from "../../../components/Skeleton/Skeleton";
import { ApiError, describeError } from "../../../lib/errors";
import { formatRelativeShort } from "../../../lib/format";
import {
  adjustScore,
  deleteScore,
  lbKeys,
  listCycleTop,
  listCycles,
} from "../api";
import type { Cycle, Entry } from "../types";
import styles from "./CyclesTab.module.css";

// ──────────────────────────────────────────────────────────────────────────────
// Avatar palette — matches the mockup's gradient-pair scheme. Mirrors the same
// helper used in LeaderboardDetailPage.tsx::AVATAR_PALETTE.
// TODO(unify): extract avatar palette to lib/avatars.ts
// ──────────────────────────────────────────────────────────────────────────────

const AVATAR_PALETTE: Array<[string, string]> = [
  ["#14b8a6", "#0d9488"], // teal
  ["#3b82f6", "#1d4ed8"], // blue
  ["#f59e0b", "#d97706"], // amber
  ["#8b5cf6", "#6366f1"], // violet
  ["#ef4444", "#b91c1c"], // red
];

function avatarColors(seed: string): [string, string] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");
}

// ──────────────────────────────────────────────────────────────────────────────
// Formatters
// ──────────────────────────────────────────────────────────────────────────────

const RANGE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
});

const ABS_FMT = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

function fmtNumber(n: number | undefined | null): string {
  if (n === undefined || n === null || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-US");
}

function fmtAbs(iso: string | undefined | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return ABS_FMT.format(d);
}

function fmtRange(c: Cycle): string {
  const start = new Date(c.open_at);
  const end = c.close_at ? new Date(c.close_at) : null;
  if (Number.isNaN(start.getTime())) return "—";
  if (!end || Number.isNaN(end.getTime())) return RANGE_FMT.format(start);
  return `${RANGE_FMT.format(start)} – ${RANGE_FMT.format(end)}`;
}

/** Compact humanised diff for the cphead "Closes in 3d 4h" line. */
function humaniseUntil(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const secs = Math.floor((d.getTime() - Date.now()) / 1000);
  if (secs <= 0) return "closed";
  const days = Math.floor(secs / 86400);
  const hrs = Math.floor((secs % 86400) / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  if (days > 0) return `${days}d ${hrs}h`;
  if (hrs > 0) return `${hrs}h ${mins}m`;
  return `${mins}m`;
}

function shortId(id: string | undefined, n = 8): string {
  if (!id) return "—";
  return id.replace(/-/g, "").slice(0, n);
}

function describeQueryError(error: unknown): string {
  if (error instanceof ApiError) return describeError(error.code);
  return describeError("internal");
}

// ──────────────────────────────────────────────────────────────────────────────
// CyclesTab — entry point
// ──────────────────────────────────────────────────────────────────────────────

interface Props {
  gameId: string;
  lbId: string;
}

export function CyclesTab({ gameId, lbId }: Props) {
  const cyclesQ = useQuery({
    queryKey: lbKeys.cycles(gameId, lbId),
    queryFn: () => listCycles(gameId, lbId),
    enabled: !!gameId && !!lbId,
  });

  const cycles: Cycle[] = cyclesQ.data?.cycles ?? [];

  // Local selection. Defaults to the first `open` cycle, else the first row.
  const [selectedCycleId, setSelectedCycleId] = useState<string>("");
  useEffect(() => {
    if (selectedCycleId) return;
    if (cycles.length === 0) return;
    const open = cycles.find((c) => c.status === "open");
    setSelectedCycleId(open?.id ?? cycles[0]?.id ?? "");
  }, [cycles, selectedCycleId]);

  const selectedCycle = useMemo(
    () => cycles.find((c) => c.id === selectedCycleId),
    [cycles, selectedCycleId],
  );

  // === Loading / error / empty ====================================== //

  if (cyclesQ.isError) {
    return (
      <Alert variant="danger" title="Couldn't load cycles">
        {describeQueryError(cyclesQ.error)}
      </Alert>
    );
  }

  if (cyclesQ.isLoading) {
    return (
      <div className={styles.layout}>
        <aside className={styles.cycleList}>
          <Skeleton height={220} />
        </aside>
        <section className={styles.cyclePanel}>
          <Skeleton height={320} />
        </section>
      </div>
    );
  }

  if (cycles.length === 0) {
    // Mockup state at L1427 — brand-new leaderboard, no cycles yet.
    return (
      <div className={styles.emptyCard}>
        <div className={styles.emptyState}>
          <span className={styles.emptyIcon} aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
          </span>
          <h3>No cycles yet</h3>
          {/* TODO(next-cycle-eta): if backend exposes a computed
              next_cycle_open_at on Leaderboard, render concrete date here
              (mockup shows "First cycle starts at Mon Jun 2 00:00 +03"). */}
          <p>
            When the first window opens, it will appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <CycleListPane
        cycles={cycles}
        selectedId={selectedCycleId}
        onSelect={setSelectedCycleId}
      />
      <CyclePanel
        gameId={gameId}
        lbId={lbId}
        cycle={selectedCycle}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Left pane — cycle list
// ──────────────────────────────────────────────────────────────────────────────

function CycleListPane({
  cycles,
  selectedId,
  onSelect,
}: {
  cycles: Cycle[];
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <aside className={styles.cycleList}>
      <div className={styles.cycleListHead}>
        <span>Cycles</span>
        <span className={styles.cycleListCount}>{cycles.length}</span>
      </div>
      {cycles.map((c) => {
        const selected = c.id === selectedId;
        return (
          <button
            key={c.id}
            type="button"
            className={`${styles.cycleItem} ${selected ? styles.cycleItemSelected : ""}`}
            aria-pressed={selected}
            onClick={() => onSelect(c.id)}
          >
            <div className={styles.cycleRow}>
              <span className={styles.cid}>{c.id}</span>
              <CycleStatusPill status={c.status} />
            </div>
            <span className={styles.cycleRange}>{fmtRange(c)}</span>
          </button>
        );
      })}
    </aside>
  );
}

function CycleStatusPill({ status }: { status: Cycle["status"] }) {
  const cls =
    status === "open"
      ? styles.cycleOpen
      : status === "closing"
        ? styles.cycleClosing
        : status === "scheduled"
          ? styles.cycleScheduled
          : status === "archived"
            ? styles.cycleArchived
            : styles.cycleClosed;
  return (
    <span className={`${styles.statusPill} ${cls}`}>
      <span className={styles.statusDot} aria-hidden="true" />
      {status}
    </span>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Right pane — selected cycle detail with TopTable
// ──────────────────────────────────────────────────────────────────────────────

function CyclePanel({
  gameId,
  lbId,
  cycle,
}: {
  gameId: string;
  lbId: string;
  cycle: Cycle | undefined;
}) {
  const topQ = useQuery({
    queryKey: lbKeys.top(gameId, lbId, cycle?.id ?? "", { limit: 50 }),
    queryFn: () => {
      if (!cycle) return Promise.reject(new Error("no cycle"));
      return listCycleTop(gameId, lbId, cycle.id, { limit: 50 });
    },
    enabled: !!cycle,
  });

  const entries: Entry[] = topQ.data?.entries ?? [];

  // === Modal state ================================================== //
  const [modal, setModal] = useState<
    | { kind: "delete"; entry: Entry }
    | { kind: "adjust"; entry: Entry }
    | null
  >(null);

  if (!cycle) {
    return (
      <section className={styles.cyclePanel}>
        <div className={styles.cyclePanelBody}>
          <div className={styles.placeholder}>Select a cycle.</div>
        </div>
      </section>
    );
  }

  // === cphead time-line — depends on cycle.status =================== //
  // open/closing → "Closes in 3d 4h"; scheduled → "Opens <abs>";
  // closed/archived → "Closed <abs>". Avoids the "Closes in closed" nonsense
  // that `humaniseUntil()` would otherwise produce on a past close_at.
  let timeLine: React.ReactNode = null;
  if (cycle.status === "open" || cycle.status === "closing") {
    timeLine = cycle.close_at && (
      <>
        <span className={styles.sep}>·</span>
        <span className={styles.meta} title={fmtAbs(cycle.close_at)}>
          Closes in {humaniseUntil(cycle.close_at)}
        </span>
      </>
    );
  } else if (cycle.status === "scheduled") {
    timeLine = (
      <>
        <span className={styles.sep}>·</span>
        <span className={styles.meta}>Opens {fmtAbs(cycle.open_at)}</span>
      </>
    );
  } else {
    // closed | archived
    timeLine = cycle.close_at && (
      <>
        <span className={styles.sep}>·</span>
        <span className={styles.meta}>Closed {fmtAbs(cycle.close_at)}</span>
      </>
    );
  }

  return (
    <section className={styles.cyclePanel}>
      {/* TODO(cycle-rewards-status): add rewards-status chip ("Pending
          materialization" / "Materialized N grants") when Cycle DTO exposes
          materialized_count + status. Source-of-truth would be
          GET /reward_grants?cycle_id=… (Phase 5.2b). */}
      <div className={styles.cphead}>
        <span className={styles.cidPill}>{cycle.id}</span>
        <span className={styles.sep}>·</span>
        <span className={styles.meta} title={fmtAbs(cycle.open_at)}>
          Opened {fmtAbs(cycle.open_at)}
        </span>
        {timeLine}
        <span className={styles.sep}>·</span>
        <span className={styles.meta}>
          {fmtNumber(cycle.participants)} participants
        </span>
        <CycleStatusPill status={cycle.status} />
      </div>

      {topQ.isError ? (
        <div className={styles.cyclePanelBody}>
          <div className={styles.cardError}>
            Couldn&apos;t load top — {describeQueryError(topQ.error)}
          </div>
        </div>
      ) : topQ.isLoading ? (
        <div className={styles.cyclePanelBody}>
          <Skeleton height={220} />
        </div>
      ) : entries.length === 0 ? (
        cycle.status === "archived" ? (
          <ArchivedEmptyState />
        ) : (
          <div className={styles.cyclePanelBody}>
            <div className={styles.placeholder}>No entries in this cycle.</div>
          </div>
        )
      ) : (
        <TopTable
          entries={entries}
          onDelete={(entry) => setModal({ kind: "delete", entry })}
          onAdjust={(entry) => setModal({ kind: "adjust", entry })}
        />
      )}

      {modal?.kind === "delete" && (
        <DeleteScoreModal
          gameId={gameId}
          lbId={lbId}
          cycle={cycle}
          entry={modal.entry}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === "adjust" && (
        <AdjustScoreModal
          gameId={gameId}
          lbId={lbId}
          cycle={cycle}
          entry={modal.entry}
          onClose={() => setModal(null)}
        />
      )}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// TopTable with per-row moderation kebab menu
// ──────────────────────────────────────────────────────────────────────────────

function TopTable({
  entries,
  onDelete,
  onAdjust,
}: {
  entries: Entry[];
  onDelete: (e: Entry) => void;
  onAdjust: (e: Entry) => void;
}) {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th style={{ width: 56 }}>Rank</th>
          <th>Entity</th>
          <th className={styles.num}>Score</th>
          <th style={{ width: 120 }}>Updated at</th>
          <th style={{ width: 40 }} aria-hidden="true" />
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <TopRow
            key={e.player.id}
            entry={e}
            onDelete={() => onDelete(e)}
            onAdjust={() => onAdjust(e)}
          />
        ))}
      </tbody>
    </table>
  );
}

function TopRow({
  entry,
  onDelete,
  onAdjust,
}: {
  entry: Entry;
  onDelete: () => void;
  onAdjust: () => void;
}) {
  const rankCls =
    entry.rank === 1
      ? styles.rankGold
      : entry.rank === 2
        ? styles.rankSilver
        : entry.rank === 3
          ? styles.rankBronze
          : "";
  const [a, b] = avatarColors(entry.player.id);

  return (
    <tr>
      <td className={`${styles.rank} ${rankCls}`}>{entry.rank}</td>
      <td>
        <div className={styles.entityCell}>
          <span
            className={styles.avatar}
            style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}
            aria-hidden="true"
          >
            {initials(entry.player.display_name)}
          </span>
          {/* TODO(player-handle): expose handle on EntryPlayer once
              /v1/leaderboards/{lb}/top backend supports ?expand=handle.
              Mockup shows "Alice @alice" — handle is not in Entry DTO yet. */}
          <span className={styles.entityName}>{entry.player.display_name}</span>
        </div>
      </td>
      <td className={`${styles.num} ${styles.score}`}>{fmtNumber(entry.score)}</td>
      <td className={styles.muted} title={entry.updated_at}>
        {formatRelativeShort(entry.updated_at)}
      </td>
      <td className={styles.actionCell}>
        <RowMenu onDelete={onDelete} onAdjust={onAdjust} />
      </td>
    </tr>
  );
}

function RowMenu({
  onDelete,
  onAdjust,
}: {
  onDelete: () => void;
  onAdjust: () => void;
}) {
  return (
    <Menu
      aria-label="Score actions"
      placement="bottom-end"
      items={[
        {
          label: "Delete score",
          icon: <Trash2 size={14} aria-hidden="true" />,
          variant: "danger",
          onSelect: onDelete,
        },
        {
          label: "Adjust score…",
          icon: <Edit3 size={14} aria-hidden="true" />,
          onSelect: onAdjust,
        },
      ]}
    />
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Archived empty-state (mockup L1411–1417)
// ──────────────────────────────────────────────────────────────────────────────

function ArchivedEmptyState() {
  return (
    <div className={styles.emptyState}>
      <span className={styles.emptyIcon} aria-hidden="true">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <polyline points="21 8 21 21 3 21 3 8" />
          <rect x="1" y="3" width="22" height="5" />
          <line x1="10" y1="12" x2="14" y2="12" />
        </svg>
      </span>
      <h3>Top data archived</h3>
      <p>
        Redis cache for this cycle was evicted. Aggregate counts and audit are
        preserved in Postgres.
      </p>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Modals
// ──────────────────────────────────────────────────────────────────────────────

const REASON_HELPER =
  "Reason is collected here for operator-side workflow records. Backend persistence (a reason column on the audit row) is planned as a Phase 5.3d backend extension; until then this field is informational only.";

/** Invalidate the queries that depend on the just-mutated cycle. */
function useInvalidateCycle(gameId: string, lbId: string, cycleId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({
      queryKey: ["leaderboard.top", gameId, lbId, cycleId],
    });
    qc.invalidateQueries({ queryKey: lbKeys.cycle(gameId, lbId, cycleId) });
    qc.invalidateQueries({
      // Match every audit filter under this cycle without forcing callers to
      // know the exact key shape (which embeds the AuditListFilter).
      predicate: (q) =>
        q.queryKey[0] === "leaderboard.audit" &&
        q.queryKey[1] === gameId &&
        q.queryKey[2] === lbId &&
        q.queryKey[3] === cycleId,
    });
  };
}

function DeleteScoreModal({
  gameId,
  lbId,
  cycle,
  entry,
  onClose,
}: {
  gameId: string;
  lbId: string;
  cycle: Cycle;
  entry: Entry;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const invalidate = useInvalidateCycle(gameId, lbId, cycle.id);

  const mutation = useMutation({
    mutationFn: ({ entityId }: { entityId: string }) =>
      deleteScore(gameId, lbId, cycle.id, entityId),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? describeError(err.code) : describeError("internal"),
      );
    },
  });

  const canSubmit = reason.trim().length > 0 && !mutation.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title="Delete score?"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => mutation.mutate({ entityId: entry.player.id })}
            disabled={!canSubmit}
            loading={mutation.isPending}
          >
            Delete
          </Button>
        </>
      }
    >
      <div className={styles.modalIconRow}>
        <span className={`${styles.modalIcon} ${styles.modalIconDanger}`} aria-hidden="true">
          <Trash2 size={16} />
        </span>
      </div>

      <p className={styles.modalBodyText}>
        This will remove <b>{entry.player.display_name}</b>{" "}
        (player_id: <code>{shortId(entry.player.id)}-…</code>)&apos;s score from{" "}
        <code>{cycle.id}</code>. The action is logged to the audit channel as{" "}
        <code>admin</code>.
      </p>

      <label className={styles.modalLabel} htmlFor="delete-reason">
        Reason
      </label>
      <textarea
        id="delete-reason"
        className={styles.textarea}
        placeholder="e.g., Manual rollback after exploit report #2841…"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
      />
      <span className={styles.helper}>{REASON_HELPER}</span>

      {error && (
        <div className={styles.errorBlurb} role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}

function AdjustScoreModal({
  gameId,
  lbId,
  cycle,
  entry,
  onClose,
}: {
  gameId: string;
  lbId: string;
  cycle: Cycle;
  entry: Entry;
  onClose: () => void;
}) {
  const [reason, setReason] = useState("");
  const [newScore, setNewScore] = useState<string>(String(entry.score));
  const [error, setError] = useState<string | null>(null);
  const invalidate = useInvalidateCycle(gameId, lbId, cycle.id);

  const newScoreId = "adjust-new-score";

  const mutation = useMutation({
    mutationFn: ({ entityId, score }: { entityId: string; score: number }) =>
      adjustScore(gameId, lbId, cycle.id, entityId, { score }),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (err) => {
      setError(
        err instanceof ApiError ? describeError(err.code) : describeError("internal"),
      );
    },
  });

  // Validation: finite integer (positive or zero). NaN/empty blocks submit.
  const parsed = Number(newScore);
  const scoreValid =
    newScore.trim().length > 0 &&
    Number.isFinite(parsed) &&
    Number.isInteger(parsed);
  const canSubmit = scoreValid && reason.trim().length > 0 && !mutation.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title="Adjust score"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() =>
              mutation.mutate({ entityId: entry.player.id, score: parsed })
            }
            disabled={!canSubmit}
            loading={mutation.isPending}
          >
            Save changes
          </Button>
        </>
      }
    >
      <div className={styles.modalIconRow}>
        <span className={`${styles.modalIcon} ${styles.modalIconAccent}`} aria-hidden="true">
          <Edit3 size={16} />
        </span>
      </div>

      <p className={styles.modalBodyText}>
        Set a new score for <b>{entry.player.display_name}</b>{" "}
        (player_id: <code>{shortId(entry.player.id)}-…</code>) in{" "}
        <code>{cycle.id}</code>. The audit log will record both the old and new
        values.
      </p>

      <div className={styles.modalGrid2}>
        <div className={styles.modalField}>
          <label className={styles.modalLabel}>Old score</label>
          <span className={styles.roPill}>{fmtNumber(entry.score)}</span>
        </div>
        <div className={styles.modalField}>
          <label className={styles.modalLabel} htmlFor={newScoreId}>
            New score
          </label>
          <input
            id={newScoreId}
            type="number"
            inputMode="numeric"
            className={`${styles.input} ${styles.inputMono}`}
            value={newScore}
            onChange={(e) => setNewScore(e.target.value)}
          />
        </div>
      </div>

      <label className={styles.modalLabel} htmlFor="adjust-reason">
        Reason
      </label>
      <textarea
        id="adjust-reason"
        className={styles.textarea}
        placeholder="e.g., Reverted +340 boost from disputed match #5512…"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
      />
      <span className={styles.helper}>{REASON_HELPER}</span>

      {error && (
        <div className={styles.errorBlurb} role="alert">
          {error}
        </div>
      )}
    </Modal>
  );
}
